import { CommonActions } from '@react-navigation/native';
import { router, useFocusEffect, useNavigation } from 'expo-router';
import type { UnknownInputParams } from 'expo-router';
import { useCallback, useRef } from 'react';

const PATHNAME_BY_SCREEN = {
  index: '/store-locator',
  'service-mode': '/store-locator/service-mode',
} as const;

type StoreLocatorScreen = keyof typeof PATHNAME_BY_SCREEN;

/**
 * Forwards to another screen of the store locator, carrying `params` onto it. Used to send the
 * legacy URLs to `/store-locator` when v2 is on, and `/store-locator` back to the legacy
 * `service-mode` when it is off.
 *
 * A direct visit builds this stack with its initial `service-mode` screen underneath the page,
 * holding a copy of the page's params. Replacing only the page would leave that placeholder in
 * history as the back target, so the whole locator entry is replaced instead, leaving only
 * `screen`.
 *
 * The entry is swapped through a reset rather than `StackActions.replace`: a replace hands back
 * any locator preloaded in the parent stack (home warms one on the pickup tab) as it is,
 * dropping `params`, so a guest forwarded to delivery would land on pickup.
 *
 * @param params Search params for the destination. Read on focus rather than tracked, so the
 * caller can pass a fresh object every render.
 * @param enabled Whether to forward at all.
 * @param screen The locator screen to forward to.
 */
export function useForwardToStoreLocator(
  params: UnknownInputParams,
  enabled: boolean,
  screen: StoreLocatorScreen = 'index'
) {
  const navigation = useNavigation();
  const paramsRef = useRef(params);
  paramsRef.current = params;

  useFocusEffect(
    useCallback(() => {
      if (!enabled) {
        return;
      }

      const parent = navigation.getParent();
      const parentState = parent?.getState();
      const parentRoute = parentState?.routes[parentState.index ?? 0];

      if (parent && parentRoute?.name === 'store-locator') {
        parent.dispatch(state => {
          const routes = state.routes.map((route, index) =>
            index === state.index
              ? { name: 'store-locator', params: { screen, params: paramsRef.current } }
              : route
          );
          return CommonActions.reset({ ...state, routes });
        });
        return;
      }

      // Only reachable if these routes stop sitting directly under a `store-locator` entry.
      // Forwarding with a leftover screen behind is still better than stranding the guest here.
      router.replace({ pathname: PATHNAME_BY_SCREEN[screen], params: paramsRef.current });
    }, [enabled, navigation, screen])
  );
}
